import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "./OSGridMap.scss";
import proj4 from "proj4";

import { OSGridOverlay } from "./OSGridOverlay";
import CustomPopupContent from "./CustomPopupContent";
import { FitBoundsToMarkers } from "./FitBoundsToMarkers";
import "leaflet/dist/leaflet.css";

import {
  ensureValidCoordinates,
  formatOSGridRef,
  createModernPinIcon,
  isValidOSGridCoordinate,
  getCenterFromMarkers,
  isValidLatLngCoordinate,
  convertLatLngToOSGrid,
  convertOSGridToLatLng,
} from "./OSGridUtils";

// Custom hook to handle map events
const MapEvents = ({ onMouseMove, onClick }) => {
  const map = useMap();

  useEffect(() => {
    const handleMouseMove = (e) => onMouseMove(e.latlng);
    const handleClick = (e) => onClick(e.latlng);

    map.on("mousemove", handleMouseMove);
    map.on("click", handleClick);

    return () => {
      map.off("mousemove", handleMouseMove);
      map.off("click", handleClick);
    };
  }, [map, onMouseMove, onClick]);

  return null;
};

// Custom component to handle focusing on selected marker
const FocusOnSelectedMarker = ({ selectedMarker, markers, gpsLogs }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedMarker) return;

    // Find the marker to focus on
    let targetMarker = null;

    // Check if it's a GPS log marker
    if (selectedMarker.type === "gps" && selectedMarker.index !== undefined) {
      const gpsLog = gpsLogs[selectedMarker.index];
      if (
        gpsLog &&
        gpsLog.coordinates &&
        isValidOSGridCoordinate(gpsLog.coordinates)
      ) {
        targetMarker = convertOSGridToLatLng(gpsLog.coordinates);
      }
    }
    // Check if it's a user marker
    else if (
      selectedMarker.type === "user" &&
      selectedMarker.index !== undefined
    ) {
      const userMarker = markers[selectedMarker.index];
      if (userMarker && isValidLatLngCoordinate(userMarker)) {
        targetMarker = userMarker;
      }
    }
    // Direct coordinate provided
    else if (selectedMarker.lat && selectedMarker.lng) {
      targetMarker = selectedMarker;
    }

    if (targetMarker) {
      // Focus on the marker with animation
      map.flyTo(
        [targetMarker.lat, targetMarker.lng],
        selectedMarker.zoom || 15,
        {
          duration: 1.5,
          easeLinearity: 0.25,
        }
      );
    }
  }, [selectedMarker, markers, gpsLogs, map]);

  return null;
};

// Helper to check if two coordinates are the same (within a small threshold)
const isSameLocation = (coord1, coord2, threshold = 0.0001) => {
  if (!coord1 || !coord2) return false;

  return (
    Math.abs(coord1.lat - coord2.lat) < threshold &&
    Math.abs(coord1.lng - coord2.lng) < threshold
  );
};

const OSGridMap = ({
  lat,
  lng,
  marker,
  setMarker,
  flag = "single",
  gpsLogs = [],
  setGuessData,
  level = "rookie",
  onCoordinateSelect,
  selectedMarker = null, // New prop for selected marker
  blinkDuration = 3000, // New prop for blink duration in ms
}) => {
  const [hoverPosition, setHoverPosition] = useState(null);
  const [hoverOSGrid, setHoverOSGrid] = useState(null);
  const [markers, setMarkers] = useState([]);
  // const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [blinkingMarkerId, setBlinkingMarkerId] = useState(null);
  const mapRef = useRef();

  // Create pin icons
  const existingPinIcon = createModernPinIcon("default");
  const userPinIcon = createModernPinIcon("user");
  const correctPinIcon = createModernPinIcon("correct");
  const incorrectPinIcon = createModernPinIcon("incorrect");

  // Handle blinking animation when selectedMarker changes
  useEffect(() => {
    if (!selectedMarker) {
      setBlinkingMarkerId(null);
      return;
    }

    // Generate the marker ID based on the selected marker
    let markerId = null;
    if (selectedMarker.type === "gps" && selectedMarker.index !== undefined) {
      markerId = `gps-${selectedMarker.index}`;
    } else if (
      selectedMarker.type === "user" &&
      selectedMarker.index !== undefined
    ) {
      markerId = `user-${selectedMarker.index}`;
    }

    if (markerId) {
      setBlinkingMarkerId(markerId);

      // Remove blink class after specified duration
      const timer = setTimeout(() => {
        setBlinkingMarkerId(null);
      }, blinkDuration);

      return () => clearTimeout(timer);
    }
  }, [selectedMarker, blinkDuration]);

  // Sync markers from parent
  useEffect(() => {
    if (flag === "single") {
      const cleanMarker = marker ? ensureValidCoordinates(marker) : null;
      setMarkers(cleanMarker ? [cleanMarker] : []);
    } else {
      const cleanMarkers = Array.isArray(marker)
        ? marker.map((m) => ensureValidCoordinates(m)).filter(Boolean)
        : [];
      setMarkers(cleanMarkers);
    }
  }, [marker, flag]);

  // Center map on new marker when added
  useEffect(() => {
    if (mapRef.current && markers.length > 0) {
      const latestMarker = markers[markers.length - 1]; // Get the most recent marker
      if (isValidLatLngCoordinate(latestMarker)) {
        mapRef.current.setView([latestMarker.lat, latestMarker.lng], 15); // Center on new marker with zoom level 15
      }
    }
  }, [markers]);

  // Determine center based on all valid coordinates
  const calculatedCenter = getCenterFromMarkers(markers, gpsLogs);

  // Fix default Leaflet icon paths
  delete L.Icon.Default.prototype._getIconUrl;

  const handleMouseMove = (latlng) => {
    setHoverPosition(latlng);
    // Convert lat/lng to OS Grid for display
    const osGrid = convertLatLngToOSGrid(latlng);
    setHoverOSGrid(osGrid);
  };

  const handleMarkerClick = (clickedMarker) => {
    if (flag === "single") {
      setMarkers([]);
      setMarker(null);
    } else {
      const newMarkers = markers.filter(
        (m) => !isSameLocation(m, clickedMarker)
      );
      const convertedMarkers = newMarkers.map((m) => {
        const osGrid = convertLatLngToOSGrid({
          lat: typeof m.lat === "number" ? m.lat : parseFloat(m.lat),
          lng: typeof m.lng === "number" ? m.lng : parseFloat(m.lng),
        });
        return {
          loc_x: osGrid ? osGrid.x : null,
          loc_y: osGrid ? osGrid.y : null,
          alt:
            typeof m.alt === "number" ? m.alt : m.alt ? parseFloat(m.alt) : 0,
        };
      });
      setGuessData(convertedMarkers);
      setMarkers(newMarkers);
      setMarker(newMarkers);
    }
  };

  const handleClick = (latlng) => {
    // Convert lat/lng to OS Grid coordinates for the callback
    const osGrid = convertLatLngToOSGrid(latlng);

    // Keep existing click handling for other use cases
    if (flag === "single") {
      setMarkers([latlng]);
      setMarker(latlng);
    } else if (
      (level === "intermediate" && markers.length >= 3) ||
      (level === "advanced" && markers.length >= 6)
    ) {
      return; // Don't add more markers if limit reached
    } else {
      if (onCoordinateSelect && osGrid) {
        onCoordinateSelect(osGrid);
        return; // Don't add marker automatically, let parent handle it
      }
      const newMarkers = [...markers, latlng];
      setMarkers(newMarkers);
      setMarker(newMarkers);
    }
  };

  const transformCoords = function (arr) {
    return proj4("EPSG:27700", "EPSG:4326", arr).reverse();
  };

  const getMarkerPinIcon = (marker) => {
    if (marker.correct === true) {
      return correctPinIcon;
    } else if (marker.correct === false) {
      return incorrectPinIcon;
    } else {
      return userPinIcon;
    }
  };

  // Helper function to get marker class names
  const getMarkerClassName = (markerId) => {
    let className = "marker";
    if (blinkingMarkerId === markerId) {
      className += " blink";
    }
    return className;
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapContainer
        zoom={10}
        scrollWheelZoom={true}
        zoomControl={false}
        ref={mapRef}
        center={calculatedCenter}
        className={`field-map ${level}`}
        style={{ backgroundColor: "transparent" }}
        maxBounds={L.latLngBounds(
          transformCoords([-238375.0, 0.0]),
          transformCoords([900000.0, 1376256.0])
        )}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='© <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        <OSGridOverlay />
        <FitBoundsToMarkers markers={markers} gpsLogs={gpsLogs} />
        <FocusOnSelectedMarker
          selectedMarker={selectedMarker}
          markers={markers}
          gpsLogs={gpsLogs}
        />

        {/* GPS Log Markers - Convert from OS Grid to LatLng */}
        {gpsLogs.map((log, index) => {
          // Skip logs with null coordinates
          if (!log.coordinates) {
            return null;
          }

          // Check if coordinates are valid OS Grid coordinates
          if (!isValidOSGridCoordinate(log.coordinates)) {
            return null;
          }

          // Convert OS Grid to LatLng for display
          const latLngCoord = convertOSGridToLatLng(log.coordinates);
          if (!latLngCoord) {
            return null;
          }

          const markerId = `gps-${index}`;
          const gridRef = formatOSGridRef(
            latLngCoord.originalX,
            latLngCoord.originalY
          );

          return (
            <Marker
              key={markerId}
              position={[latLngCoord.lat, latLngCoord.lng]}
              icon={existingPinIcon}
              className="marker"
              eventHandlers={
                {
                  // mouseover: () => setHoveredMarkerId(markerId),
                  // mouseout: () => setHoveredMarkerId(null),
                }
              }
            >
              <Popup>
                <CustomPopupContent
                  title="Dropped Pin"
                  gridRef={gridRef}
                  coordinates={{ lat: latLngCoord.lat, lng: latLngCoord.lng }}
                  elevation={log.coordinates.alt || 0}
                  showActions={false}
                  // onViewDetails={() => console.log("View details clicked")}
                  // onRemovePin={() => {
                  //   console.log("Remove pin clicked");
                  // }}
                />
              </Popup>
            </Marker>
          );
        })}

        {/* User Markers - Only show when not displaying feedback */}
        {markers.map((markerPosition, index) => {
          const markerId = `user-${index}`;
          const osGrid = convertLatLngToOSGrid(markerPosition);
          const gridRef = osGrid
            ? formatOSGridRef(osGrid.x, osGrid.y)
            : "Unknown";

          return (
            <Marker
              key={markerId}
              position={[markerPosition.lat, markerPosition.lng]}
              icon={getMarkerPinIcon(markerPosition)}
              className={getMarkerClassName(markerId)}
              eventHandlers={
                {
                  // mouseover: () => setHoveredMarkerId(markerId),
                  // mouseout: () => setHoveredMarkerId(null),
                }
              }
            >
              <Popup>
                <CustomPopupContent
                  title="Your Pin"
                  gridRef={gridRef}
                  coordinates={{
                    lat: markerPosition.lat,
                    lng: markerPosition.lng,
                  }}
                  elevation={markerPosition?.alt}
                  onViewDetails={() => console.log("View details clicked")}
                  onRemovePin={() => handleMarkerClick(markerPosition)}
                  showActions={markerPosition.isCorrect === null}
                />
              </Popup>
            </Marker>
          );
        })}

        <MapEvents onMouseMove={handleMouseMove} onClick={handleClick} />
      </MapContainer>

      {/* Hover display showing OS Grid coordinates instead of lat/lng */}
      {hoverPosition && hoverOSGrid && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "5px 10px",
            borderRadius: "5px",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          x: {hoverOSGrid.x}, y: {hoverOSGrid.y}
        </div>
      )}
    </div>
  );
};

export default OSGridMap;
